import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import CircularProgress from '@material-ui/core/CircularProgress'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'

import { AgentActivityTrail } from '../../core/types/agentStatus.types'
import {
  ActionSelections,
  EphemeralChatSurface,
  FlowAction,
  MessageBubbleFollowUpConfig,
} from '../../core/types/chat.types'
import { MaxChatMessage } from '../../types'
import { maxMessageDebug } from '../../utils/max-debug'
import AgentActivityBubble from './agent-activity-bubble'
import EphemeralSurfaceHost from './ephemeral-surface-host'
import MessageBubble from './message-bubble'
import TypingIndicator from './typing-indicator'

type MessagesContainerProps = {
  messages: MaxChatMessage[]
  isLoading: boolean
  isTyping: boolean
  activeAgentTrail?: AgentActivityTrail | null
  agentTrailForMessage?: (message: MaxChatMessage) => AgentActivityTrail | null
  ephemeralSurface?: EphemeralChatSurface
  isMessageFavorited?: (message: MaxChatMessage) => boolean
  onSelectFlowAction: (payload: {
    action: FlowAction
    message: MaxChatMessage
    selections?: ActionSelections
  }) => void
  onToolkitFavorite: (payload: { message: MaxChatMessage }) => void
  onToolkitShare: (payload: { message: MaxChatMessage }) => void
  onToolkitRetry: (payload: { message: MaxChatMessage }) => void
  onToolkitExportBudget: (payload: { message: MaxChatMessage; action: FlowAction }) => void
  onToolkitExportReport?: (payload: { message: MaxChatMessage; action: FlowAction }) => void
  onSubmitEphemeralSurface?: (surface: EphemeralChatSurface) => void
  onAttachFiles?: (files: FileList | File[]) => void
}

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  loadingHost: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
  transcript: {
    width: '100%',
    maxWidth: '60rem',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  // The closed reasoning line sits directly above its answer, aligned with the bubble
  // (40px avatar + 12px gap).
  anchoredTrail: {
    marginBottom: theme.spacing(1),
    paddingLeft: 52,
  },
}))

const formatStepLabel = (step: string): string =>
  step
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

const isBudgetStepSelectionMessage = (message: MaxChatMessage): boolean => {
  const flow = message.flowMetadata?.flow?.toUpperCase()
  const step = message.flowMetadata?.step?.toUpperCase()
  return flow === 'BUDGET' && Boolean(step) && step !== 'COMPLETE'
}

const MessagesContainer = ({
  messages,
  isLoading,
  isTyping,
  activeAgentTrail,
  agentTrailForMessage,
  ephemeralSurface,
  isMessageFavorited,
  onSelectFlowAction,
  onToolkitFavorite,
  onToolkitShare: _onToolkitShare,
  onToolkitRetry: _onToolkitRetry,
  onToolkitExportBudget,
  onToolkitExportReport,
  onSubmitEphemeralSurface,
  onAttachFiles,
}: MessagesContainerProps) => {
  const classes = useStyles()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const messageElementsRef = useRef<Map<string, HTMLDivElement>>(new Map())
  const seenMessageIdsRef = useRef<Set<string>>(new Set())
  const hasInitializedScrollRef = useRef(false)
  const shouldKeepTypingVisibleRef = useRef(false)
  const scrollFrameIdRef = useRef<number | null>(null)
  const [disabledActionMessageIds, setDisabledActionMessageIds] = useState<Set<string>>(new Set())

  const messageIdsKey = useMemo(() => messages.map((message) => message.id).join('|'), [messages])

  const currentConversationId = messages[0]?.conversationId || ''

  const setMessageElementRef = useCallback((messageId: string, element: HTMLDivElement | null) => {
    if (!element) {
      messageElementsRef.current.delete(messageId)
      return
    }

    messageElementsRef.current.set(messageId, element)
  }, [])

  const scheduleScroll = useCallback((callback: () => void) => {
    if (scrollFrameIdRef.current !== null) {
      cancelAnimationFrame(scrollFrameIdRef.current)
    }

    scrollFrameIdRef.current = requestAnimationFrame(() => {
      scrollFrameIdRef.current = null
      callback()
    })
  }, [])

  const scrollToBottom = useCallback(() => {
    scheduleScroll(() => {
      if (!containerRef.current) return
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    })
  }, [scheduleScroll])

  const scrollToMessageTop = useCallback((messageId: string) => {
    scheduleScroll(() => {
      const container = containerRef.current
      const messageElement = messageElementsRef.current.get(messageId)
      if (!container || !messageElement) return

      const containerRect = container.getBoundingClientRect()
      const messageRect = messageElement.getBoundingClientRect()
      const targetTop = container.scrollTop + (messageRect.top - containerRect.top)

      container.scrollTo({
        top: targetTop,
        behavior: 'auto',
      })
    })
  }, [scheduleScroll])

  useEffect(() => {
    return () => {
      if (scrollFrameIdRef.current !== null) {
        cancelAnimationFrame(scrollFrameIdRef.current)
        scrollFrameIdRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    setDisabledActionMessageIds(new Set())
  }, [currentConversationId])

  useEffect(() => {
    const ids = messages.map((message) => message.id)

    if (!hasInitializedScrollRef.current) {
      seenMessageIdsRef.current = new Set(ids)
      hasInitializedScrollRef.current = true
      scrollToBottom()
      return
    }

    const previousIds = seenMessageIdsRef.current
    const newMessages = messages.filter((message) => !previousIds.has(message.id))
    seenMessageIdsRef.current = new Set(ids)

    const latestNewUserMessage = [...newMessages].reverse().find((message) => message.role === 'user')
    if (latestNewUserMessage) {
      shouldKeepTypingVisibleRef.current = true
      scrollToBottom()
      return
    }

    const latestNewAssistantMessage = [...newMessages]
      .reverse()
      .find((message) => message.role !== 'user')

    if (latestNewAssistantMessage) {
      shouldKeepTypingVisibleRef.current = false
      scrollToMessageTop(latestNewAssistantMessage.id)
    }
  }, [messageIdsKey, scrollToBottom, scrollToMessageTop])

  useEffect(() => {
    if (!isTyping) {
      shouldKeepTypingVisibleRef.current = false
      return
    }

    if (shouldKeepTypingVisibleRef.current) {
      scrollToBottom()
    }
  }, [isTyping, scrollToBottom])

  // New reasoning steps follow the typing-indicator rule: keep the bottom in view only while the
  // user has just sent a message.
  const activeTrailStepCount = activeAgentTrail?.steps.length ?? 0
  const previousActiveTrailStepCountRef = useRef(activeTrailStepCount)
  useEffect(() => {
    const previousStepCount = previousActiveTrailStepCountRef.current
    previousActiveTrailStepCountRef.current = activeTrailStepCount
    if (activeTrailStepCount <= previousStepCount || !shouldKeepTypingVisibleRef.current) return
    scrollToBottom()
  }, [activeTrailStepCount, scrollToBottom])

  // Scroll to bottom when an ephemeral surface appears
  const prevEphemeralKindRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const kind = ephemeralSurface?.kind
    if (kind === 'analyze-upload-form' && prevEphemeralKindRef.current !== kind) {
      scrollToBottom()
    }
    prevEphemeralKindRef.current = kind
  }, [ephemeralSurface?.kind, scrollToBottom])

  const shouldDisableActions = useCallback(
    (message: MaxChatMessage): boolean => {
      if (disabledActionMessageIds.has(message.id)) {
        return true
      }

      const messageIndex = messages.findIndex((candidate) => candidate.id === message.id)
      if (messageIndex === -1) {
        return true
      }

      return messageIndex < messages.length - 1
    },
    [disabledActionMessageIds, messages]
  )

  const buildFollowUpConfig = useCallback(
    (message: MaxChatMessage): MessageBubbleFollowUpConfig | undefined => {
      if (message.role === 'user') return undefined

      const actions = message.flowMetadata?.availableActions
      if (!message.isFlow && (!actions || !actions.length)) return undefined

      const messageIndex = messages.findIndex((m) => m.id === message.id)
      const hasMessagesAfter = messageIndex !== -1 && messageIndex < messages.length - 1
      if (hasMessagesAfter) return undefined

      const isBudgetStepSelection = isBudgetStepSelectionMessage(message)
      const step = message.flowMetadata?.step

      return {
        actions: actions ?? [],
        disabled: shouldDisableActions(message),
        conversationId: message.conversationId,
        flowMetadata: message.flowMetadata,
        variant: isBudgetStepSelection ? 'budget-question' : 'default',
        stepLabel: isBudgetStepSelection && step ? formatStepLabel(step) : undefined,
      }
    },
    [messages, shouldDisableActions]
  )

  useEffect(() => {
    const lastMessage = messages[messages.length - 1]
    maxMessageDebug.ui('MessagesContainer render expectation', {
      conversationId: currentConversationId || null,
      isLoading,
      isTyping,
      shouldShowTypingIndicator: !isLoading && isTyping,
      lastMessageId: lastMessage?.id || null,
      lastMessageRole: lastMessage?.role || null,
      messages,
    })
  }, [currentConversationId, isLoading, isTyping, messageIdsKey, messages])

  const handleActionSelect = useCallback(
    (payload: { action: FlowAction; message: MaxChatMessage; selections?: ActionSelections }) => {
      maxMessageDebug.event('MessagesContainer action click captured', {
        conversationId: payload.message.conversationId,
        messageId: payload.message.id,
        actionValue: payload.action.actionValue,
        actionType: payload.action.actionType,
        selections: payload.selections || null,
      })
      setDisabledActionMessageIds((current) => {
        const next = new Set(current)
        next.add(payload.message.id)
        return next
      })

      onSelectFlowAction(payload)
    },
    [onSelectFlowAction]
  )

  return (
    <div ref={containerRef} className={classes.root}>
      {isLoading && (
        <div className={classes.loadingHost}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="textSecondary">
            Loading messages...
          </Typography>
        </div>
      )}

      {!isLoading && (
        <div className={classes.transcript}>
          {messages.map((message) => {
            const followUp = buildFollowUpConfig(message)
            const isFavorited = Boolean(message.fav) || Boolean(isMessageFavorited?.(message))
            const anchoredTrail =
              message.role !== 'user' ? agentTrailForMessage?.(message) ?? null : null

            return (
              <div key={message.id} ref={(element) => setMessageElementRef(message.id, element)}>
                {anchoredTrail ? (
                  <AgentActivityBubble trail={anchoredTrail} className={classes.anchoredTrail} />
                ) : null}
                <MessageBubble
                  message={message}
                  followUp={followUp}
                  isFavorited={isFavorited}
                  onSelectAction={(payload) => handleActionSelect(payload)}
                  onToolkitFavorite={onToolkitFavorite}
                  onToolkitExportBudget={onToolkitExportBudget}
                  onToolkitExportReport={onToolkitExportReport}
                />
              </div>
            )
          })}

          {ephemeralSurface && onSubmitEphemeralSurface && onAttachFiles && (
            <EphemeralSurfaceHost
              surface={ephemeralSurface}
              onSubmitSurface={onSubmitEphemeralSurface}
              onAttachFiles={onAttachFiles}
            />
          )}

          {activeAgentTrail ? (
            <AgentActivityBubble trail={activeAgentTrail} showAvatar />
          ) : isTyping ? (
            <TypingIndicator />
          ) : null}
        </div>
      )}
    </div>
  )
}

export default MessagesContainer
